import { Link, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useState } from "react";
import { FaAsterisk, FaEye, FaEyeSlash } from "react-icons/fa";
import authService from "../services/authService";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const labelClassName =
  "mb-2 flex items-center gap-1 text-sm font-semibold text-slate-200";

const ChangePass = () => {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    new_password_confirm: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate password match
    if (formData.new_password !== formData.new_password_confirm) {
      setError("New passwords do not match.");
      return;
    }

    // Check if not authenticated
    if (!authService.isAuthenticated()) {
      setError("Please login to change your password.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    setLoading(true);

    try {
       const response = await authService.changePassword({
    old_password: formData.old_password,
    new_password: formData.new_password,
    new_password_confirm: formData.new_password_confirm,
  });

  if (response.status === "success") {
    setSuccess(response.message || "Password changed successfully!");
    setError("");
    setFormData({
      old_password: "",
      new_password: "",
      new_password_confirm: "",
    });

    setTimeout(() => {
      navigate("/research");
    }, 2000);
  } else {
    const errorDetails = response.errors
      ? Object.entries(response.errors)
          .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`
          )
          .join(" | ")
      : "";

    setSuccess("");
    setError(
      errorDetails
        ? `${response.message || "Password change failed"} - ${errorDetails}`
        : response.message || "Password change failed"
    );
  }
    } catch (err) {
      console.error("Change Password Error:", err);
      setError(err.message || "Failed to change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
  
    <main className="min-h-screen bg-[#111318] text-slate-200">
        <Header />

        <section className="flex justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          <div className="w-full max-w-[560px]">
            <form
              className="rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8"
              onSubmit={handleSubmit}
            >
              <div className="mb-6 text-center sm:mb-7">
                <h2 className="text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[30px]">
                  Change Password
                </h2>
              </div>

              {error && (
                <div className="mb-5 rounded-2xl border border-rose-400/35 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-100">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-5 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-100">
                  {success}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="old-password" className={labelClassName}>
                  Old Password <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
                </label>
                <div className="relative">
                  <input
                    id="old-password"
                    type={showOldPassword ? "text" : "password"}
                    name="old_password"
                    placeholder="Old Password"
                    className={`${inputClassName} pr-12`}
                    value={formData.old_password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                    aria-label={showOldPassword ? "Hide old password" : "Show old password"}
                  >
                    {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="new-password" className={labelClassName}>
                  New Password <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    name="new_password"
                    placeholder="New Password"
                    className={`${inputClassName} pr-12`}
                    value={formData.new_password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                    aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="confirm-new-password" className={labelClassName}>
                  Confirm New Password <FaAsterisk className="mt-0.5 text-[8px] text-rose-400" aria-hidden="true" />
                </label>
                <div className="relative">
                  <input
                    id="confirm-new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    name="new_password_confirm"
                    placeholder="Confirm New Password"
                    className={`${inputClassName} pr-12`}
                    value={formData.new_password_confirm}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
              >
                {loading ? "Changing..." : "Change Password"}
              </button>

              <div className="mt-5 text-center text-sm text-slate-400">
                <Link
                  to="/profile"
                  className="font-semibold text-blue-400 transition hover:text-blue-300"
                >
                  ← Back to Profile
                </Link>
              </div>
            </form>
          </div>
        </section>
    </main>
     <Footer />
      </>
  );
};

export default ChangePass;
